/**
 * Bar replay: hiding the future so a chart can be read the way it was lived.
 *
 * <h2>One transform, at the source</h2>
 * Every study, indicator and overlay in this application reads the same `barData`. So the whole of
 * "recompute strictly up to the revealed boundary" is achieved by masking THAT — not by teaching
 * each consumer a cutoff. A per-consumer cutoff would be a lookahead leak waiting to happen: the
 * one study nobody remembered to change would quietly see the future and score beautifully.
 *
 * <h2>The mask is flat bars, not truncation</h2>
 * Truncating the array would move the chart's right edge on every step, so the viewport would jump
 * and a study's bar indices would shift underneath any overlay already drawn. Replacing the future
 * with one-price bars at the revealed close keeps every index, date and axis position exactly where
 * it was, and the drawn result is a flat line — visibly "nothing here yet" rather than a chart that
 * silently got shorter.
 *
 * <h2>The bar array is [open, close, low, high]</h2>
 * ECharts' candlestick order, NOT the order the name OHLC implies. A flat bar is the same number
 * four times so the mask itself cannot get this wrong — but anything reading a masked bar back must
 * know, and `revealedClose` below is the reason this file has the constants.
 */

/** Positions within the chart's own bar array. */
export const OPEN = 0;
export const CLOSE = 1;
export const LOW = 2;
export const HIGH = 3;

const isNum = v => typeof v === 'number' && Number.isFinite(v);

/** How many bars a chart needs before replaying it means anything. */
export const MIN_BARS_TO_SIMULATE = 5;

/** A bar index brought inside the series, or null when there is no series to be inside of. */
export function clampRevealIndex(index, length) {
  if (!Number.isFinite(length) || length <= 0) return null;
  if (!Number.isFinite(index)) return null;
  return Math.max(0, Math.min(Math.round(index), length - 1));
}

/** The close at the revealed bar — the price everything after it is frozen at. */
export function revealedClose(barData, revealIndex) {
  const bar = barData?.ohlc?.[revealIndex];
  return Array.isArray(bar) && isNum(bar[CLOSE]) ? bar[CLOSE] : null;
}

/**
 * The series with everything after `revealIndex` flattened to that bar's close.
 *
 * <p>Returns the ORIGINAL object untouched when there is nothing to mask, so a caller can compare
 * by reference to know whether anything happened, and React does not re-render for a no-op.
 *
 * <p>Volume goes to zero rather than being left alone. A volume bar standing under a flat price bar
 * would be the one part of the future still on screen, and it is exactly the part a trader reads
 * for what is about to happen.
 *
 * @param {{ohlc: Array<Array<number>>, dates: Array<string>, volumes?: Array<number>}} barData
 * @param {number} revealIndex the last bar whose real prices are shown
 */
export function maskBarsAfter(barData, revealIndex) {
  const ohlc = barData?.ohlc;
  if (!Array.isArray(ohlc) || ohlc.length === 0) return barData;

  const at = clampRevealIndex(revealIndex, ohlc.length);
  if (at === null || at >= ohlc.length - 1) return barData;

  const close = revealedClose(barData, at);
  // Without a usable close there is nothing to flatten TO. Masking to zero would draw a cliff to
  // the axis floor and rescale the whole chart, which reads as a data failure rather than a hidden
  // future — so the honest answer is to leave the series alone and let the caller report it.
  if (close === null) return barData;

  const flat = [close, close, close, close];
  return {
    ...barData,
    ohlc: ohlc.map((bar, i) => (i <= at ? bar : [...flat])),
    volumes: Array.isArray(barData.volumes)
      ? barData.volumes.map((v, i) => (i <= at ? v : 0))
      : barData.volumes,
  };
}

/** Whether a bar is one of the mask's own — all four prices identical. */
export function isFlatBar(bar) {
  return Array.isArray(bar) && bar.length >= 4 && isNum(bar[0])
    && bar[0] === bar[1] && bar[1] === bar[2] && bar[2] === bar[3];
}

/**
 * The first masked bar, found by comparing against the original rather than by looking for flatness.
 *
 * <p>A real trading day CAN print open = high = low = close — a limit-locked move, or a thinly
 * traded name. Treating flatness as the marker would report the boundary somewhere inside real
 * history on exactly those charts, so the two series are compared instead.
 *
 * @returns {number} index of the first masked bar, or -1 when nothing is masked
 */
export function firstMaskedIndex(original, masked) {
  const a = original?.ohlc;
  const b = masked?.ohlc;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return -1;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (!Array.isArray(x) || !Array.isArray(y)) continue;
    if (x[OPEN] !== y[OPEN] || x[CLOSE] !== y[CLOSE]
        || x[LOW] !== y[LOW] || x[HIGH] !== y[HIGH]) return i;
  }
  return -1;
}

/**
 * Where a click should move the boundary.
 *
 * <h2>The two sides of the edge do different things, and that asymmetry is the point</h2>
 *
 * <ul>
 *   <li><b>On the hidden side</b> (past the edge): the boundary jumps to the clicked bar. You are
 *       naming how far forward to reveal, which is the only way to say it.</li>
 *   <li><b>On the revealed side</b> (at or behind the edge): the boundary advances by exactly ONE
 *       bar. It does not rewind.</li>
 * </ul>
 *
 * <p>This used to be symmetric — the boundary simply became whatever bar you clicked — and the
 * symmetry was the problem. Nearly all of the chart in a replay is revealed, so nearly every click
 * landed behind the edge and <em>destroyed</em> the replay, throwing away every bar between the
 * click and the edge. A stray click cost the whole session, and the gesture that costs you the most
 * was the easiest one to make by accident.
 *
 * <p>Advancing instead makes the common click the safe one: anywhere on the revealed side means
 * "next bar", which is what a replay is mostly doing. Nothing here can move the boundary backwards
 * any more — the arrow keys still step both ways, which is a deliberate gesture rather than a
 * misplaced click, and leaving the mode restores everything.
 *
 * <h2>THE CTS STOP, and why it is ADDITIVE on both sides rather than a new rule</h2>
 *
 * A timing point is where the reader wants the playhead to pause, so a click now stops at the next
 * one instead of walking past it. Each side keeps its own fallback, which is what makes this purely
 * additive — no gesture loses the meaning it had:
 *
 * <ul>
 *   <li><b>Hidden side:</b> the first CTS point strictly inside <code>(edge, clicked]</code>,
 *       otherwise the clicked bar exactly as before. So clicking the very next hidden bar still
 *       advances by one — there is no timing point in a span of one bar to stop at.</li>
 *   <li><b>Revealed side:</b> the next CTS point AHEAD of the edge, otherwise one bar on. This is
 *       the repeated-click gesture, and it is the one the request is about: each click lands on
 *       the next timing point rather than crawling a bar at a time.</li>
 * </ul>
 *
 * <p>The revealed-side jump is deliberately UNBOUNDED forward — the clicked bar is behind the
 * edge, so it cannot bound anything, and "jump directly to that next CTS point" is the whole
 * feature. It still only ever moves FORWARD: a CTS point behind the edge is never a target,
 * because revealing backwards would throw away the session, which is the defect the asymmetry
 * above exists to prevent. ArrowRight remains the exactly-one-bar gesture.
 *
 * @param clickedIndex the bar that was clicked
 * @param currentReveal the boundary now, or null before the first click
 * @param length the real series' length
 * @param ctsIndices bar indices of the timing points, ASCENDING; omit for the pre-CTS behaviour
 * @returns {number|null} the new reveal index, or null when the click changes nothing
 */
export function revealTargetFor(clickedIndex, currentReveal, length, ctsIndices) {
  const clicked = clampRevealIndex(clickedIndex, length);
  if (clicked === null) return null;

  /*
   * THE FIRST CLICK IS NOT AN ADVANCE, so no timing point stops it.
   *
   * <p>Found by running it. With no boundary the span is the whole series from bar 0, so the
   * first timing point in HISTORY was the nearest one "on the way" — a click near bar 8,000 set
   * the boundary to bar 2 and masked the entire chart. The request is about advancing a
   * playhead; before the first click there is no playhead, only an operator saying where to put
   * one. That is the gesture `clicked` already expresses exactly.
   */
  if (currentReveal === null || currentReveal === undefined) return clicked;

  // Ahead of the edge: stop at the first timing point on the way, else reveal up to the bar named.
  if (clicked > currentReveal) {
    const stop = nextCtsIndex(ctsIndices, currentReveal, clicked);
    return stop === null ? clicked : stop;
  }

  // At or behind it: the next timing point ahead, else one bar on. Returns null at the last bar,
  // where there is nothing to add.
  const ahead = nextCtsIndex(ctsIndices, currentReveal, length - 1);
  if (ahead !== null) return ahead;
  return stepReveal(currentReveal, 1, length);
}

/**
 * The first timing point strictly after `after` and at or before `limit`.
 *
 * <p>STRICTLY after, so the point the boundary is already sitting on cannot be its own target — a
 * click there would otherwise be a no-op the caller reads as a move. Inclusive of `limit`, because
 * naming a bar that IS a timing point should land on it rather than one short.
 *
 * <p>It takes the MINIMUM qualifying index rather than the first one it meets, so the answer does
 * not depend on the caller having sorted the list. An unsorted list would otherwise overshoot
 * silently — the playhead landing on a later point than the one it should have stopped at, which
 * reads as a timing point the scan missed rather than as an ordering bug here.
 *
 * <p>A plain scan rather than a binary search: these lists are a few hundred long at most (254 on
 * SPY after the 4-bar fold) and this runs once per click, so the clarity is worth more than the
 * logarithm.
 *
 * @returns {number|null} the index, or null when the span holds no timing point
 */
export function nextCtsIndex(ctsIndices, after, limit) {
  if (!Array.isArray(ctsIndices) || ctsIndices.length === 0) return null;
  if (!Number.isFinite(limit)) return null;
  // Before the first click there is no boundary, so every point from bar 0 is still ahead.
  const from = Number.isFinite(after) ? after : -1;
  let best = null;
  for (const raw of ctsIndices) {
    if (!Number.isFinite(raw)) continue;
    const at = Math.round(raw);
    if (at > from && at <= limit && (best === null || at < best)) best = at;
  }
  return best;
}

/** One step, clamped, for the arrow keys. */
export function stepReveal(currentReveal, delta, length) {
  const next = clampRevealIndex((currentReveal ?? 0) + delta, length);
  return next === null || next === currentReveal ? null : next;
}
